/**
 * NS color tokens as TypeScript exports for MUI theme consumption.
 *
 * Single source of truth: values match tokens.css clusters 1-4 + 9-12.
 * Strikt typed const objects, 6-digit hex strings only (no rgba/var()).
 *
 * IMPORTANT: when tokens.css color values change, update this file in sync.
 * Test colors.test.ts 2.3 enforces consistency for 8 spot-check tokens
 * (one per cluster, light + dark).
 *
 * SKIPPED tokens (rationale):
 *   - rgba()-valued tokens (alpha/subtle/disabled/textVerySubtle/...) — not
 *     6-digit hex; consumed via CSS custom properties only.
 *   - linear-gradient() values (--ns-bursts-gradient-linear) — not a color.
 *   - --ns-bursts-font-color / slack-channel-text — pure white, not surfaced
 *     as a colors.ts key (covered by general fg tokens).
 *   - cluster 9 nav tokens that resolve to rgba in dark (e.g. nav-item-hover-bg
 *     dark = bg-hover = rgba) — only navIndicator is exposed (resolves to
 *     bg-interactive-default which is hex in both modes).
 */

export const colors = {
  dark: {
    bgAccentBlueDefault: '#5d9fea',
    bgAccentGreenDefault: '#47bd99',
    bgAccentOrangeDefault: '#ff9544',
    bgAccentPinkDefault: '#ea66aa',
    bgAccentPurpleDefault: '#a880ef',
    bgAccentRedDefault: '#f06666',
    bgAccentYellowDefault: '#fed652',
    bgBrandPrimary: '#252528',
    bgBrandSecondary: '#9ac5f1',
    bgDefault: '#0d0e10',
    bgDefaultInverse: '#d6d7dc',
    bgDestructiveDefault: '#ff9496',
    bgDiscovery: '#a880ef',
    bgElevated1: '#191a1c',
    bgElevated2: '#252528',
    bgEmphasisPrimary: '#1f2c42',
    bgEmphasisSecondary: '#333438',
    bgErrorDefault: '#ff9496',
    bgInfoDefault: '#5d9fea',
    bgInput: '#000000',
    bgInteractiveDefault: '#5d9fea',
    bgInteractiveHover: '#9ac5f1',
    bgInteractivePressed: '#c4dbf7',
    bgSuccessDefault: '#4ecda6',
    bgSunken: '#000000',
    bgWarningDefault: '#ff9544',
    borderError: '#ff9496',
    borderFocus: '#5d9fea',
    borderInfo: '#5d9fea',
    borderInteractiveDefault: '#5d9fea',
    borderInteractiveHover: '#9ac5f1',
    borderInteractivePressed: '#c4dbf7',
    borderSelectedDefault: '#fed652',
    borderSelectedEmphasis: '#fed652',
    borderSuccess: '#4ecda6',
    borderWarning: '#ff9544',
    burstsBgDefault: '#0a3680',
    chartCategory1: '#9ac5f1',
    chartCategory2: '#2b87a6',
    chartCategory3: '#fed652',
    chartCategory4: '#f6a2cf',
    chartCategory5: '#885dd0',
    chartCategory6: '#8dd9ec',
    chartCategory7: '#fff2c8',
    chartCategory8: '#d53483',
    chartError: '#eb0f38',
    chartNeutral: '#75767c',
    chartSuccess: '#47bd99',
    chartWarning: '#ff9544',
    codeBg: '#000000',
    fgDestructiveDefault: '#ff9496',
    fgEmphasis: '#e4e5e8',
    fgError: '#ff9496',
    fgGold: '#FFE066',
    fgInfo: '#5d9fea',
    fgInteractiveDefault: '#5d9fea',
    fgInteractiveHover: '#9ac5f1',
    fgInteractivePressed: '#c4dbf7',
    fgLogo: '#003082',
    fgSelected: '#ffffff',
    fgSuccess: '#4ecda6',
    fgWarning: '#ff9544',
    navIndicator: '#5d9fea',
    statusAborted: '#9e9e9e',
    statusPending: '#75767c',
    statusRunning: '#9ac5f1',
  },
  light: {
    bgAccentBlueDefault: '#0062d1',
    bgAccentGreenDefault: '#3e7f67',
    bgAccentOrangeDefault: '#f05c00',
    bgAccentPinkDefault: '#d53483',
    bgAccentPurpleDefault: '#885dd0',
    bgAccentRedDefault: '#eb0f38',
    bgAccentYellowDefault: '#ffc70f',
    bgBrandPrimary: '#ffc70f',
    bgBrandSecondary: '#0a3680',
    bgDefault: '#ffffff',
    bgDefaultInverse: '#333438',
    bgDestructiveDefault: '#ce092d',
    bgDiscovery: '#885dd0',
    bgElevated1: '#ffffff',
    bgElevated2: '#ffffff',
    bgEmphasisPrimary: '#ebf5ff',
    bgEmphasisSecondary: '#f0f1f3',
    bgErrorDefault: '#ce092d',
    bgInfoDefault: '#0062d1',
    bgInput: '#ffffff',
    bgInteractiveDefault: '#0062d1',
    bgInteractiveHover: '#0c44a1',
    bgInteractivePressed: '#0a3680',
    bgSuccessDefault: '#3e7f67',
    bgSunken: '#f5f5f7',
    bgWarningDefault: '#f05c00',
    borderError: '#ce092d',
    borderFocus: '#0062d1',
    borderInfo: '#0062d1',
    borderInteractiveDefault: '#0062d1',
    borderInteractiveHover: '#0c44a1',
    borderInteractivePressed: '#0a3680',
    borderSelectedDefault: '#0062d1',
    borderSelectedEmphasis: '#0a3680',
    borderSuccess: '#3e7f67',
    borderWarning: '#f05c00',
    burstsBgDefault: '#0a3680',
    chartCategory1: '#0c44a1',
    chartCategory2: '#33a0c4',
    chartCategory3: '#b69016',
    chartCategory4: '#be2771',
    chartCategory5: '#a880ef',
    chartCategory6: '#236d86',
    chartCategory7: '#6a5618',
    chartCategory8: '#ea66aa',
    chartError: '#ce092d',
    chartNeutral: '#919197',
    chartSuccess: '#3e9d7b',
    chartWarning: '#f05c00',
    codeBg: '#f5f5f7',
    fgDestructiveDefault: '#ce092d',
    fgEmphasis: '#0a3680',
    fgError: '#ce092d',
    fgGold: '#FFD600',
    fgInfo: '#0062d1',
    fgInteractiveDefault: '#0062d1',
    fgInteractiveHover: '#0c44a1',
    fgInteractivePressed: '#0a3680',
    fgLogo: '#0a3680',
    fgSelected: '#0062d1',
    fgSuccess: '#3e7f67',
    fgWarning: '#f05c00',
    navIndicator: '#0062d1',
    statusAborted: '#757575',
    statusPending: '#919197',
    statusRunning: '#0c44a1',
  },
} as const;

export type ColorTokens = typeof colors;
